import React from "react";
import { Button, Modal } from "antd";
import { svg } from "../Assets/svg";

export interface ModalAction {
  label: string;
  onClick: () => void | Promise<void>;
  variant: "primary" | "secondary" | "danger" | "red";
  loading?: boolean;
}

export interface WarningsModalProps {
  warnings: any[];
  title?: string;
  subtitle?: string | React.ReactNode;
  actions?: ModalAction[];

  // Legacy props for backwards compatibility
  onClose: () => void;
  onContinue?: () => void;
  loading?: boolean;
  isBackAction?: boolean;
  onDiscard?: () => void;
  hideContinue?: boolean;
}

export const WarningActionLayouts = {
  BackNavigation: (
    onStay: () => void,
    onDiscard: () => void,
  ): ModalAction[] => [
    { label: "Stay on Page", variant: "secondary", onClick: onStay },
    { label: "Leave Without Saving", variant: "red", onClick: onDiscard },
  ],
  BypassableSave: (
    onClose: () => void,
    onForceSave: () => void,
    isSaving: boolean,
  ): ModalAction[] => [
    { label: "Close", variant: "secondary", onClick: onClose },
    {
      label: "Save & Continue",
      variant: "primary",
      onClick: onForceSave,
      loading: isSaving,
    },
  ],
  Blocking: (onClose: () => void): ModalAction[] => [
    { label: "Close", variant: "secondary", onClick: onClose },
  ],
};

export const WarningsModal: React.FC<WarningsModalProps> = ({
  warnings,
  title = "Warning",
  subtitle = "There are errors in your data.",
  actions,

  // Legacy props
  onClose,
  onContinue,
  loading = false,
  isBackAction = false,
  onDiscard,
  hideContinue = true,
}) => {
  // If actions are provided, use them. Otherwise, calculate legacy actions.
  const resolvedActions: ModalAction[] = actions || [];

  console.log("Warnings: ", warnings);

  if (!actions) {
    if (isBackAction) {
      resolvedActions.push({
        label: "Stay on Page",
        variant: "secondary",
        onClick: onClose,
      });
      if (onDiscard) {
        resolvedActions.push({
          label: "Leave Without Saving",
          variant: "red",
          onClick: onDiscard,
        });
      }
    } else {
      resolvedActions.push({
        label: "Close",
        variant: "secondary",
        onClick: onClose,
      });
      if (!hideContinue && onContinue) {
        resolvedActions.push({
          label: "Save & Continue",
          variant: "primary",
          onClick: onContinue,
          loading,
        });
      }
    }
  }

  return (
    <Modal
      open={warnings.length > 0}
      title={
        <div className="text-center fs-4 fw-bolder lh-m text-warning">
          {title}
        </div>
      }
      footer={null}
      onCancel={onClose}
      centered
      width={"40rem"}
      closeIcon={svg?.closeIcon}
    >
      <div className="d-flex flex-column gap-3">
        <div className="fs-6 text-center">{subtitle}</div>
        <div style={{ maxHeight: "300px", overflowY: "auto" }}>
          {warnings.map((w: any, index: number) =>
            ["CHECKLIST_ANSWERS_MISSING", "DELIVERY_REQUIRED"].includes(
              w.code,
            ) ? (
              <div
                key={index}
                className="alert alert-warning py-2 mb-2"
                style={{ whiteSpace: "pre-line" }}
              >
                <strong>{w.main_message || "Product"}</strong>
              </div>
            ) : (
              <div
                key={index}
                className="alert alert-warning py-2 mb-2"
                style={{ whiteSpace: "pre-line" }}
              >
                <strong>{w.main_message || "Product"}:</strong> {w.message}
              </div>
            ),
          )}
        </div>

        {(isBackAction ||
          resolvedActions.some((a) => a.label === "Leave Without Saving")) && (
          <p className="fs-6 text-center">
            Clicking “Leave Without Saving” will discard all unsaved changes and
            take you back. Are you sure you want to continue?
          </p>
        )}
      </div>
      <div className="row gy-2 justify-content-center mt-4">
        {resolvedActions.map((action, index) => {
          let btnClass = "btn btn-lg h-auto text-uppercase w-100";
          if (action.variant === "primary") btnClass += " btn-primary";
          else if (action.variant === "secondary")
            btnClass += " btn-outline-secondary";
          else if (action.variant === "danger" || action.variant === "red")
            btnClass += " btn-red";

          return (
            <div className="col-sm-5">
            <Button
              key={index}
              onClick={action.onClick}
              className={btnClass}
              loading={action.loading}
              
            >
              {action.label}
            </Button>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};
